import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import type { LessonCanvasDocument, LessonCanvasElement } from '@/lib/ai';
import { readableBoardColor } from '@/lib/lessonPalette';

interface Props {
  currentTime: number;
  lessonCanvas: LessonCanvasDocument;
  title: string;
}

const graphSamples: Record<string, (x: number) => number | null> = {
  quadratic: (x) => x * x,
  linear: (x) => x,
  absolute: (x) => Math.abs(x),
  sine: (x) => Math.sin(x),
  cosine: (x) => Math.cos(x),
  reciprocal: (x) => Math.abs(x) < 0.12 ? null : 1 / x,
  exponential: (x) => Math.pow(2, x),
  logarithmic: (x) => x <= 0 ? null : Math.log2(x),
  cubic: (x) => x * x * x,
};

function GraphDiagram({ element, darkMode }: { element: LessonCanvasElement; darkMode: boolean }) {
  const data = element.diagramData || {};
  const kind = data.graphFunction || 'linear';
  const p = data.graphParameters || {};
  const points = (data.points || []).filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y)).slice(0, 8);
  const axisColor = darkMode ? '#e5e7eb' : '#183153';
  const gridColor = darkMode ? '#334155' : '#dbe5ec';
  const labelColor = darkMode ? '#cbd5e1' : '#52606d';
  const curveColor = darkMode ? '#38bdf8' : readableBoardColor(element.color, '#1d4ed8');
  const asymptoteColor = darkMode ? '#fbbf24' : '#d97706';
  const pointColor = darkMode ? '#86efac' : '#e06c47';
  const a = Number.isFinite(p.a) ? p.a! : 1;
  const b = Number.isFinite(p.b) ? p.b! : 0;
  const c = Number.isFinite(p.c) ? p.c! : 0;
  const h = Number.isFinite(p.h) ? p.h! : 0;
  const k = Number.isFinite(p.k) ? p.k! : 0;
  const angleFactor = data.angleUnit === 'degrees' ? Math.PI / 180 : 1;
  const fn = kind === 'quadratic'
    ? (x: number) => a * x * x + b * x + c
    : kind === 'cubic'
      ? (x: number) => a * x * x * x + b * x + c
      : kind === 'linear'
        ? (x: number) => a * x + b
        : kind === 'absolute'
          ? (x: number) => a * Math.abs(x - h) + k
          : kind === 'sine'
            ? (x: number) => (p.amplitude ?? 1) * Math.sin((p.frequency ?? 1) * (x - (p.phase ?? 0)) * angleFactor) + k
            : kind === 'cosine'
              ? (x: number) => (p.amplitude ?? 1) * Math.cos((p.frequency ?? 1) * (x - (p.phase ?? 0)) * angleFactor) + k
              : kind === 'reciprocal'
                ? (x: number) => Math.abs(x - h) < 0.12 ? null : a / (x - h) + k
                : kind === 'exponential'
                  ? (x: number) => a * Math.pow(p.base ?? 2, x - h) + k
                  : kind === 'logarithmic'
                    ? (x: number) => x - h <= 0 ? null : a * (Math.log(x - h) / Math.log(p.base ?? 2)) + k
                    : graphSamples[kind] || graphSamples.linear;
  const defaultXMin = kind === 'sine' || kind === 'cosine' ? (data.angleUnit === 'degrees' ? -360 : -2 * Math.PI) : -5;
  const defaultXMax = kind === 'sine' || kind === 'cosine' ? (data.angleUnit === 'degrees' ? 360 : 2 * Math.PI) : 5;
  const xMin = Number.isFinite(data.xMin) && data.xMin! < (data.xMax ?? defaultXMax) ? data.xMin! : defaultXMin;
  const xMax = Number.isFinite(data.xMax) && data.xMax! > xMin ? data.xMax! : defaultXMax;
  const pointYValues = points.map((point) => point.y);
  const defaultYMin = kind === 'quadratic' || kind === 'exponential' || kind === 'logarithmic' ? -1 : -5;
  const defaultYMax = kind === 'quadratic' || kind === 'exponential' ? 10 : kind === 'sine' || kind === 'cosine' ? 1.5 : 5;
  const yMin = Number.isFinite(data.yMin) ? data.yMin! : Math.min(defaultYMin, ...(pointYValues.length ? pointYValues.map((value) => value - 1) : [defaultYMin]));
  const yMax = Number.isFinite(data.yMax) && data.yMax! > yMin ? data.yMax! : Math.max(defaultYMax, ...(pointYValues.length ? pointYValues.map((value) => value + 1) : [defaultYMax]));
  const left = 74;
  const right = 970;
  const top = 24;
  const bottom = 254;
  const px = (x: number) => left + ((x - xMin) / (xMax - xMin)) * (right - left);
  const py = (y: number) => bottom - ((y - yMin) / (yMax - yMin)) * (bottom - top);
  let path = '';
  let drawing = false;
  for (let i = 0; i <= 220; i += 1) {
    const x = xMin + (i / 220) * (xMax - xMin);
    const y = fn(x);
    if (y === null || !Number.isFinite(y) || y < yMin - 2 || y > yMax + 2) {
      drawing = false;
      continue;
    }
    path += `${drawing ? ' L' : ' M'}${px(x).toFixed(1)} ${py(y).toFixed(1)}`;
    drawing = true;
  }
  const xAxisY = Math.max(top, Math.min(bottom, py(0)));
  const yAxisX = Math.max(left, Math.min(right, px(0)));
  const createTicks = (min: number, max: number, requested: number | undefined) => {
    const rawStep = requested && requested > 0 ? requested : (max - min) / 8;
    const first = Math.ceil(min / rawStep) * rawStep;
    const values: number[] = [];
    for (let value = first; value <= max + rawStep * 0.01 && values.length < 15; value += rawStep) values.push(value);
    return values;
  };
  const xTicks = createTicks(xMin, xMax, data.xTick);
  const yTicks = createTicks(yMin, yMax, data.yTick);
  const formatTick = (value: number, axis: 'x' | 'y') =>
    axis === 'x' && data.angleUnit === 'degrees' ? `${Math.round(value)}°` : Number(value.toFixed(2)).toString();
  return (
    <svg viewBox="0 0 1000 300" className="h-full w-full" role="img" aria-label={data.caption || data.equation || `${kind} graph`}>
      <defs>
        <marker id={`axis-arrow-${element.id}`} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill={axisColor} />
        </marker>
      </defs>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((index) => {
        const x = left + (index / 8) * (right - left);
        return <line key={`gx-${index}`} x1={x} y1={top} x2={x} y2={bottom} stroke={gridColor} strokeWidth="1.5" />;
      })}
      {[0, 1, 2, 3, 4, 5, 6].map((index) => {
        const y = top + (index / 6) * (bottom - top);
        return <line key={`gy-${index}`} x1={left} y1={y} x2={right} y2={y} stroke={gridColor} strokeWidth="1.5" />;
      })}
      {(data.asymptotes || []).map((line, index) => line.orientation === 'vertical'
        ? <line key={`asym-${index}`} x1={px(line.value)} y1={top} x2={px(line.value)} y2={bottom} stroke={asymptoteColor} strokeWidth="3" strokeDasharray="10 8" />
        : <line key={`asym-${index}`} x1={left} y1={py(line.value)} x2={right} y2={py(line.value)} stroke={asymptoteColor} strokeWidth="3" strokeDasharray="10 8" />)}
      <line x1={left} y1={xAxisY} x2={right} y2={xAxisY} stroke={axisColor} strokeWidth="4" markerEnd={`url(#axis-arrow-${element.id})`} />
      <line x1={yAxisX} y1={bottom} x2={yAxisX} y2={top} stroke={axisColor} strokeWidth="4" markerEnd={`url(#axis-arrow-${element.id})`} />
      {xTicks.filter((value) => Math.abs(value) > 1e-9).map((value) => (
        <g key={`xt-${value}`}>
          <line x1={px(value)} y1={xAxisY - 6} x2={px(value)} y2={xAxisY + 6} stroke={axisColor} strokeWidth="2" />
          <text x={px(value)} y={Math.min(bottom + 20, xAxisY + 23)} textAnchor="middle" fill={labelColor} fontSize="17">{formatTick(value, 'x')}</text>
        </g>
      ))}
      {yTicks.filter((value) => Math.abs(value) > 1e-9).map((value) => (
        <g key={`yt-${value}`}>
          <line x1={yAxisX - 6} y1={py(value)} x2={yAxisX + 6} y2={py(value)} stroke={axisColor} strokeWidth="2" />
          <text x={yAxisX - 10} y={py(value) + 6} textAnchor="end" fill={labelColor} fontSize="17">{formatTick(value, 'y')}</text>
        </g>
      ))}
      <path d={path} fill="none" stroke={curveColor} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((point, index) => (
        <g key={`${point.x}-${point.y}-${index}`}>
          <circle cx={px(point.x)} cy={py(point.y)} r="8" fill={pointColor} stroke={darkMode ? '#080b10' : 'white'} strokeWidth="3" />
          {point.label && <text x={px(point.x) + 12} y={py(point.y) - 10} fill={axisColor} fontSize="28" fontWeight="700">{point.label}</text>}
        </g>
      ))}
      <text x={right} y={Math.min(286, xAxisY + 27)} textAnchor="end" fill={axisColor} fontSize="30" fontWeight="700">{data.xAxisLabel || 'x'}</text>
      <text x={Math.max(25, yAxisX - 12)} y={top + 15} textAnchor="end" fill={axisColor} fontSize="30" fontWeight="700">{data.yAxisLabel || 'y'}</text>
      {data.equation && <text x={left + 12} y={top + 28} fill={curveColor} fontSize="30" fontWeight="800">{data.equation}</text>}
      {(data.caption || data.domain || data.range) && <text x="500" y="290" textAnchor="middle" fill={labelColor} fontSize="22">{[data.caption, data.domain && `Domain: ${data.domain}`, data.range && `Range: ${data.range}`].filter(Boolean).join('  ·  ')}</text>}
    </svg>
  );
}

function Diagram({ element, darkMode }: { element: LessonCanvasElement; darkMode: boolean }) {
  if (element.diagramData?.primitives?.length) {
    return (
      <svg viewBox="0 0 1000 700" className="h-full w-full" role="img" aria-label={element.diagramData.caption || element.text || 'Educational diagram'}>
        <defs>
          <marker id={`arrow-${element.id}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 Z" fill="context-stroke" />
          </marker>
        </defs>
        {element.diagramData.primitives.map((p, index) => {
          const color = readableBoardColor(p.color, '#111827');
          const shared = { stroke: color, strokeWidth: 4, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
          if (p.kind === 'line' || p.kind === 'arrow') return <line key={index} x1={p.x} y1={p.y} x2={p.x2} y2={p.y2} {...shared} markerEnd={p.kind === 'arrow' ? `url(#arrow-${element.id})` : undefined} />;
          if (p.kind === 'circle') return <circle key={index} cx={p.x} cy={p.y} r={p.radius} {...shared} />;
          if (p.kind === 'rect') return <rect key={index} x={p.x} y={p.y} width={p.width} height={p.height} {...shared} />;
          if (p.kind === 'polyline') return <polyline key={index} points={p.points?.map(v => `${v.x},${v.y}`).join(' ')} {...shared} />;
          return <text key={index} x={p.x} y={p.y} fill={color} fontSize="32">{p.text}</text>;
        })}
      </svg>
    );
  }
  const stroke = readableBoardColor(element.color, '#2c8b79');
  const label = element.labels?.[0] || '';
  const labels = element.diagramData?.labels?.length ? element.diagramData.labels : element.labels || [];
  const stages = element.diagramData?.stages || [];
  if (element.diagramKind === 'graph') {
    return <GraphDiagram element={element} darkMode={darkMode} />;
  }
  if (element.diagramKind === 'coordinate-plane') {
    const points = element.diagramData?.points || [];
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full" role="img" aria-label={element.diagramData?.caption || 'Coordinate plane'}>
        {[100, 200, 300, 400, 500, 600, 700, 800, 900].map((x) => <line key={`x-${x}`} x1={x} y1="20" x2={x} y2="280" stroke="#dbe5ec" strokeWidth="2" />)}
        {[50, 100, 150, 200, 250].map((y) => <line key={`y-${y}`} x1="40" y1={y} x2="960" y2={y} stroke="#dbe5ec" strokeWidth="2" />)}
        <line x1="40" y1="150" x2="960" y2="150" stroke="#183153" strokeWidth="5" />
        <line x1="500" y1="20" x2="500" y2="280" stroke="#183153" strokeWidth="5" />
        {points.slice(0, 8).map((point, index) => (
          <g key={`${point.x}-${point.y}-${index}`}>
            <circle cx={500 + point.x * 70} cy={150 - point.y * 35} r="10" fill="#e06c47" />
            {point.label && <text x={515 + point.x * 70} y={135 - point.y * 35} fill="#183153" fontSize="28">{point.label}</text>}
          </g>
        ))}
        <text x="950" y="178" textAnchor="end" fill="#183153" fontSize="30">{element.diagramData?.xAxisLabel || 'x'}</text>
        <text x="480" y="38" textAnchor="end" fill="#183153" fontSize="30">{element.diagramData?.yAxisLabel || 'y'}</text>
      </svg>
    );
  }
  if (element.diagramKind === 'wave') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        <path d="M40 150 C120 25 200 25 280 150 S440 275 520 150 S680 25 760 150 S920 275 980 150" fill="none" stroke={stroke} strokeWidth="10" strokeLinecap="round" />
        <line x1="195" y1="48" x2="675" y2="48" stroke="#e06c47" strokeWidth="6" />
        <path d="M195 48 l24 -13 v26 z M675 48 l-24 -13 v26 z" fill="#e06c47" />
        <text x="435" y="35" textAnchor="middle" fill="#e06c47" fontSize="28" fontWeight="700">wavelength λ</text>
        <text x="185" y="92" fill="#183153" fontSize="28">crest</text>
        <text x="430" y="265" fill="#183153" fontSize="28">trough</text>
      </svg>
    );
  }
  if (element.diagramKind === 'reaction') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        {(stages.length ? stages : [{ label: labels[0] || 'Reactants' }, { label: labels[1] || 'Reaction' }, { label: labels[2] || 'Products' }]).slice(0, 4).map((stage, index, items) => {
          const x = 130 + index * (740 / Math.max(1, items.length - 1));
          return (
            <g key={`${stage.label}-${index}`}>
              <rect x={x - 92} y="82" width="184" height="122" rx="24" fill={['#eaf8f7', '#fff4dd', '#f2edfb', '#eaf3ff'][index]} stroke={stroke} strokeWidth="5" />
              <text x={x} y="132" textAnchor="middle" fill="#183153" fontSize="28" fontWeight="800">{stage.label.slice(0, 20)}</text>
              {stage.detail && <text x={x} y="170" textAnchor="middle" fill="#52606d" fontSize="28">{stage.detail.slice(0, 20)}</text>}
              {index < items.length - 1 && <path d={`M${x + 102} 143 H${x + 142}`} stroke="#e06c47" strokeWidth="7" markerEnd="url(#reaction-arrow)" />}
            </g>
          );
        })}
        <defs><marker id="reaction-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto"><path d="M0,0 L0,6 L9,3 z" fill="#e06c47" /></marker></defs>
        {element.diagramData?.equation && <text x="500" y="255" textAnchor="middle" fill="#402060" fontSize="28" fontWeight="800">{element.diagramData.equation}</text>}
      </svg>
    );
  }
  if (element.diagramKind === 'triangle') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        <path d="M180 255 L500 35 L820 255 Z" fill="#eaf4ff" stroke={stroke} strokeWidth="9" />
        <path d="M500 35 L500 255" stroke="#e06c47" strokeWidth="6" strokeDasharray="14 12" />
        <text x="490" y="292" fill="#183153" fontSize="28">base</text>
        <text x="515" y="145" fill="#e06c47" fontSize="28">height</text>
      </svg>
    );
  }
  if (element.diagramKind === 'cell') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        <ellipse cx="500" cy="150" rx="330" ry="125" fill="#edf9f1" stroke={stroke} strokeWidth="9" />
        <circle cx="500" cy="150" r="72" fill="#f4ecfb" stroke="#8b5fbf" strokeWidth="7" />
        <ellipse cx="325" cy="115" rx="55" ry="25" fill="#fff1e8" stroke="#e06c47" strokeWidth="5" />
        <ellipse cx="680" cy="190" rx="55" ry="25" fill="#fff1e8" stroke="#e06c47" strokeWidth="5" />
        <text x="500" y="160" textAnchor="middle" fill="#402060" fontSize="28">{labels[0] || 'nucleus'}</text>
        <path d="M245 70 L170 34" stroke="#183153" strokeWidth="3" />
        <text x="160" y="28" textAnchor="middle" fill="#183153" fontSize="28">{labels[1] || 'cell membrane'}</text>
        <path d="M325 115 L255 245" stroke="#e06c47" strokeWidth="3" />
        <text x="245" y="270" textAnchor="middle" fill="#183153" fontSize="28">{labels[2] || 'mitochondrion'}</text>
        {labels[3] && <><path d="M650 205 L765 255" stroke="#31836d" strokeWidth="3" /><text x="790" y="274" textAnchor="middle" fill="#183153" fontSize="28">{labels[3]}</text></>}
      </svg>
    );
  }
  if (element.diagramKind === 'dna') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        <path d="M230 28 C430 70 430 230 230 272 M770 28 C570 70 570 230 770 272" fill="none" stroke={stroke} strokeWidth="9" strokeLinecap="round" />
        {[55, 90, 125, 160, 195, 230].map((y, index) => {
          const offset = Math.sin(index * 1.1) * 115;
          return <line key={y} x1={500 - offset - 150} y1={y} x2={500 + offset + 150} y2={y} stroke={index % 2 ? '#e06c47' : '#8b5fbf'} strokeWidth="7" strokeLinecap="round" />;
        })}
        <text x="820" y="70" fill="#183153" fontSize="28">{labels[0] || 'double helix'}</text>
        <path d="M790 75 L700 95" stroke="#183153" strokeWidth="3" />
        <text x="810" y="235" fill="#183153" fontSize="28">{labels[1] || 'base pairs'}</text>
        <path d="M785 225 L650 195" stroke="#183153" strokeWidth="3" />
      </svg>
    );
  }
  if (element.diagramKind === 'atom') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full" role="img" aria-label={element.diagramData?.caption || 'Atomic structure'}>
        <circle cx="500" cy="150" r="48" fill="#ffd8a8" stroke="#d47b34" strokeWidth="7" />
        <text x="500" y="157" textAnchor="middle" fill="#6b3f1d" fontSize="28" fontWeight="800">{labels[0] || 'nucleus'}</text>
        {[0, 58, -58].map((rotation, index) => (
          <g key={rotation} transform={`rotate(${rotation} 500 150)`}>
            <ellipse cx="500" cy="150" rx={265 - index * 18} ry={82 + index * 12} fill="none" stroke={index % 2 ? '#8b5fbf' : stroke} strokeWidth="5" />
            <circle cx={765 - index * 18} cy="150" r="13" fill="#4878c0" stroke="white" strokeWidth="3" />
          </g>
        ))}
        <path d="M545 128 L775 55" stroke="#183153" strokeWidth="3" />
        <text x="795" y="52" fill="#183153" fontSize="28" fontWeight="700">{labels[1] || 'electron'}</text>
        <path d="M520 166 L790 235" stroke="#183153" strokeWidth="3" />
        <text x="805" y="242" fill="#183153" fontSize="28" fontWeight="700">{labels[2] || 'electron shell'}</text>
      </svg>
    );
  }
  if (element.diagramKind === 'model-3d') {
    const modelLabels = labels.length ? labels : ['front plane', 'depth', 'reference point'];
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full" role="img" aria-label={element.diagramData?.caption || 'Three-dimensional educational model'}>
        <defs>
          <linearGradient id="model-3d-top" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#dff3ff" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#b8d8f2" stopOpacity="0.7" />
          </linearGradient>
          <linearGradient id="model-3d-side" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d8c8ef" stopOpacity="0.82" />
            <stop offset="100%" stopColor="#9c82c2" stopOpacity="0.62" />
          </linearGradient>
        </defs>
        <polygon points="335,72 575,38 740,112 500,148" fill="url(#model-3d-top)" stroke={stroke} strokeWidth="6" strokeLinejoin="round" />
        <polygon points="335,72 500,148 500,260 335,184" fill="#dff2e8" fillOpacity="0.78" stroke={stroke} strokeWidth="6" strokeLinejoin="round" />
        <polygon points="500,148 740,112 740,224 500,260" fill="url(#model-3d-side)" stroke={stroke} strokeWidth="6" strokeLinejoin="round" />
        <line x1="500" y1="148" x2="500" y2="260" stroke="#183153" strokeWidth="4" strokeDasharray="10 8" />
        <circle cx="575" cy="108" r="15" fill="#e06c47" stroke="#fffefa" strokeWidth="5" />
        <path d="M575 108 L820 62" stroke="#e06c47" strokeWidth="4" />
        <text x="835" y="66" fill="#183153" fontSize="28" fontWeight="700">{modelLabels[0]?.slice(0, 18)}</text>
        <path d="M385 170 L185 225" stroke="#31836d" strokeWidth="4" />
        <text x="170" y="236" textAnchor="end" fill="#183153" fontSize="28" fontWeight="700">{modelLabels[1]?.slice(0, 18)}</text>
        <path d="M625 205 L815 248" stroke="#8b5fbf" strokeWidth="4" />
        <text x="830" y="258" fill="#183153" fontSize="28" fontWeight="700">{modelLabels[2]?.slice(0, 18)}</text>
      </svg>
    );
  }
  if (element.diagramKind === 'life-cycle') {
    const cycleStages: Array<{ label: string; detail?: string }> = stages.length
      ? stages
      : (labels.length ? labels : ['Eggs', 'Young stage', 'Adult', 'Reproduction']).map((item) => ({ label: item }));
    const positions = [[500, 52], [795, 150], [500, 250], [205, 150]];
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full" role="img" aria-label={element.diagramData?.caption || 'Life cycle'}>
        <path d="M540 55 C690 50 785 85 805 125 M800 178 C760 225 650 250 550 250 M450 250 C320 245 220 215 195 175 M195 125 C230 75 345 52 450 52" fill="none" stroke={stroke} strokeWidth="7" strokeDasharray="12 10" />
        {cycleStages.slice(0, 4).map((stage, index) => {
          const [x, y] = positions[index];
          return <g key={`${stage.label}-${index}`}><circle cx={x} cy={y} r="48" fill={['#eaf4ff', '#eef9ef', '#fff1e8', '#f4ecfb'][index]} stroke={stroke} strokeWidth="5" /><text x={x} y={y + 6} textAnchor="middle" fill="#183153" fontSize="28" fontWeight="800">{stage.label.slice(0, 12)}</text></g>;
        })}
      </svg>
    );
  }
  if (element.diagramKind === 'organ') {
    const isHeart = `${element.text || ''} ${element.diagramData?.caption || ''}`.toLowerCase().includes('heart');
    if (isHeart) {
      return (
        <svg viewBox="0 0 1000 300" className="h-full w-full" role="img" aria-label="Labelled diagram of the human heart">
          <path d="M500 258 C455 225 300 150 300 78 C300 28 360 18 400 57 C440 10 500 42 500 88 C500 42 560 10 600 57 C640 18 700 28 700 78 C700 150 545 225 500 258Z" fill="#fff0ed" stroke="#ad4164" strokeWidth="7" />
          <path d="M500 75 V225 M350 85 C405 85 440 112 468 145 M650 85 C595 85 560 112 532 145" fill="none" stroke="#183353" strokeWidth="6" />
          <path d="M360 72 V28 M640 72 V28 M390 220 V270 M610 220 V270" stroke="#245b93" strokeWidth="16" strokeLinecap="round" />
          <path d="M360 30 C335 45 335 62 360 74 M640 30 C665 45 665 62 640 74" fill="none" stroke="#177c78" strokeWidth="7" markerEnd="url(#heart-arrow)" />
          {[
            ['Right atrium', 382, 112], ['Left atrium', 618, 112],
            ['Right ventricle', 388, 183], ['Left ventricle', 612, 183],
          ].map(([label, x, y]) => <text key={String(label)} x={Number(x)} y={Number(y)} textAnchor="middle" fill="#183353" fontSize="28" fontWeight="700">{label}</text>)}
          <line x1="270" y1="96" x2="345" y2="78" stroke="#ad4164" strokeWidth="3" />
          <text x="258" y="94" textAnchor="end" fill="#ad4164" fontSize="28" fontWeight="700">deoxygenated blood</text>
          <line x1="730" y1="96" x2="655" y2="78" stroke="#177c78" strokeWidth="3" />
          <text x="742" y="94" fill="#177c78" fontSize="28" fontWeight="700">oxygenated blood</text>
          <text x="500" y="294" textAnchor="middle" fill="#754c90" fontSize="28" fontWeight="700">septum keeps the two sides separate</text>
          <defs><marker id="heart-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto"><path d="M0 0 L0 6 L9 3Z" fill="#177c78" /></marker></defs>
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        <path d="M505 52 C430 -10 300 35 310 132 C320 225 420 270 505 290 C590 270 690 225 700 132 C710 35 580 -10 505 52 Z" fill="#ffd9d2" stroke={stroke} strokeWidth="8" />
        <path d="M505 55 C470 115 540 140 500 215" fill="none" stroke="#d34f5f" strokeWidth="12" strokeLinecap="round" />
        {labels.slice(0, 4).map((item, index) => {
          const y = 65 + index * 58;
          const leftSide = index % 2 === 0;
          return <g key={`${item}-${index}`}><line x1={leftSide ? 385 : 620} y1={y + 5} x2={leftSide ? 170 : 835} y2={y} stroke="#183153" strokeWidth="3" /><text x={leftSide ? 155 : 850} y={y + 7} textAnchor={leftSide ? 'end' : 'start'} fill="#183153" fontSize="28" fontWeight="700">{item}</text></g>;
        })}
      </svg>
    );
  }
  if (element.diagramKind === 'map') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        <path d="M290 55 L410 30 L485 78 L590 55 L715 115 L675 238 L540 270 L445 235 L330 260 L245 178 Z" fill="#e8f4e4" stroke={stroke} strokeWidth="8" strokeLinejoin="round" />
        {labels.slice(0, 4).map((item, index) => {
          const positions = [[380, 100], [570, 105], [460, 200], [620, 210]][index];
          return <g key={`${item}-${index}`}><circle cx={positions[0]} cy={positions[1]} r="7" fill="#e06c47" /><text x={positions[0] + 13} y={positions[1] - 8} fill="#183153" fontSize="28" fontWeight="700">{item}</text></g>;
        })}
      </svg>
    );
  }
  if (element.diagramKind === 'none') {
    return (
      <div className="flex h-full items-center justify-center rounded-[24px] border-2 border-dashed border-[#2c8b79]/25 bg-white/60 px-16 text-center text-[28px] font-semibold leading-relaxed text-[#36566f]">
        {element.diagramData?.caption || element.text || 'This concept is best understood through the structured notes and worked example below.'}
      </div>
    );
  }
  if (element.diagramKind === 'circuit') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        <path d="M150 75 H420 M580 75 H850 V235 H150 V75" fill="none" stroke={stroke} strokeWidth="9" />
        <line x1="445" y1="40" x2="445" y2="110" stroke="#183153" strokeWidth="8" />
        <line x1="490" y1="55" x2="490" y2="95" stroke="#183153" strokeWidth="8" />
        <circle cx="715" cy="235" r="48" fill="#fff8e7" stroke="#d47b34" strokeWidth="7" />
        <path d="M685 205 L745 265 M745 205 L685 265" stroke="#d47b34" strokeWidth="6" />
        <text x="420" y="145" fill="#183153" fontSize="28">cell</text>
        <text x="675" y="298" fill="#183153" fontSize="28">lamp</text>
      </svg>
    );
  }
  if (element.diagramKind === 'force') {
    return (
      <svg viewBox="0 0 1000 300" className="h-full w-full">
        <rect x="390" y="105" width="220" height="120" rx="18" fill="#eaf4ff" stroke={stroke} strokeWidth="8" />
        <line x1="500" y1="105" x2="500" y2="30" stroke="#e06c47" strokeWidth="8" />
        <path d="M500 22 l-15 28 h30 z" fill="#e06c47" />
        <line x1="500" y1="225" x2="500" y2="290" stroke="#8b5fbf" strokeWidth="8" />
        <path d="M500 296 l-15 -28 h30 z" fill="#8b5fbf" />
        <text x="525" y="55" fill="#e06c47" fontSize="28">normal force</text>
        <text x="525" y="285" fill="#8b5fbf" fontSize="28">weight</text>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 1000 300" className="h-full w-full">
      {(stages.length ? stages : [1, 2, 3, 4].map((number): { label: string; detail?: string } => ({ label: String(number) }))).slice(0, 4).map((stage, index, items) => {
        const x = 150 + index * (705 / Math.max(1, items.length - 1));
        return (
        <g key={x}>
          <circle cx={x} cy="150" r="70" fill={['#eaf4ff', '#eef9ef', '#fff1e8', '#f4ecfb'][index]} stroke={stroke} strokeWidth="6" />
          <text x={x} y="145" textAnchor="middle" fill="#183153" fontSize="28" fontWeight="700">{stage.label.slice(0, 12)}</text>
          {stage.detail && <text x={x} y="174" textAnchor="middle" fill="#52606d" fontSize="28">{stage.detail.slice(0, 14)}</text>}
          {index < items.length - 1 && <path d={`M${x + 80} 150 H${x + 145}`} stroke="#e06c47" strokeWidth="7" markerEnd="url(#arrow)" />}
        </g>
      )})}
      <defs><marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto"><path d="M0,0 L0,6 L9,3 z" fill="#e06c47" /></marker></defs>
      {label && <text x="500" y="285" textAnchor="middle" fill="#183153" fontSize="28">{label}</text>}
    </svg>
  );
}

function renderFocusedText(
  text: string | undefined,
  focus: string | undefined,
  style: LessonCanvasElement['highlightStyle'],
) {
  const value = text || '';
  const terms = (focus || '')
    .split('|')
    .map((term) => term.trim())
    .filter((term) => term && value.includes(term))
    .sort((a, b) => b.length - a.length);
  if (!terms.length) return value;
  const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const matcher = new RegExp(`(${escaped.join('|')})`, 'g');
  const highlightClass = style === 'circle'
    ? 'lesson-focus-circle'
    : style === 'underline'
      ? 'lesson-focus-underline'
      : 'lesson-focus-marker';
  return value.split(matcher).map((part, index) =>
    terms.includes(part)
      ? <span key={`${part}-${index}`} className={highlightClass}>{part}</span>
      : part,
  );
}

function LessonElement({ element, visible, active, mathWhiteboard, darkMode, teacherBoard = false, writeProgress = 1 }: { element: LessonCanvasElement; visible: boolean; active: boolean; mathWhiteboard: boolean; darkMode: boolean; teacherBoard?: boolean; writeProgress?: number }) {
  const semanticPalette = darkMode
    ? { normal: '#f8fafc', concept: '#67e8f9', warning: '#fca5a5', result: '#86efac', emphasis: '#f0abfc' }
    : { normal: '#111827', concept: '#1d4ed8', warning: '#b91c1c', result: '#166534', emphasis: '#7e22ce' };
  const mathColor = semanticPalette[element.semanticColor || 'normal'];
  const writeOn = element.type === 'equation' || element.writingStyle === 'handwritten';
  const baseStyle = {
    left: element.x,
    top: element.y,
    width: element.width,
    height: element.height,
    color: mathColor,
    backgroundColor: 'transparent',
    fontSize: element.fontSize,
  };
  const isVisual = ['diagram', 'table', 'timeline', 'image'].includes(element.type);
  const isNote = element.type === 'note' || element.type === 'equation';
  const isHeading = element.emphasis === 'heading';
  const calloutLabel = element.calloutKind
    ? {
        important: 'Important',
        note: 'Note',
        tip: 'Tip',
        remember: 'Remember',
        'watch-out': 'Watch out',
        'quick-check': 'Quick check',
      }[element.calloutKind]
    : '';
  const className = `absolute whitespace-pre-wrap break-words leading-[1.28] transition-[box-shadow,filter] ${
    isVisual ? 'overflow-hidden' : 'overflow-visible'
  } ${isNote && !teacherBoard ? (mathWhiteboard
    ? element.calloutKind ? 'rounded-[8px] border-l-[5px] border-l-current px-8 py-6' : 'px-4 py-5'
    : 'rounded-[13px] border border-[#183353]/[0.13] px-9 py-7 shadow-[3px_5px_0_rgba(31,65,93,.08)]') : ''} ${
    element.type === 'equation' ? (mathWhiteboard ? 'lesson-math-equation font-semibold tracking-[-0.025em]' : 'lesson-note-type border-l-[8px] border-l-[#754c90]') : ''
  } ${element.writingStyle === 'handwritten' ? 'lesson-handwriting' : ''} ${
    element.emphasis === 'title' ? `${mathWhiteboard ? 'lesson-handwriting' : 'lesson-note-type'} font-semibold tracking-[-0.025em]` : isHeading ? 'lesson-label-type font-medium tracking-[0.11em]' : element.emphasis === 'answer' ? `${mathWhiteboard ? 'lesson-math-equation' : 'lesson-note-type'} font-bold text-center` : element.writingStyle === 'handwritten' ? 'font-semibold' : 'lesson-body-type font-semibold'
  }`;

  return (
    <motion.div
      style={baseStyle}
      className={className}
      initial={false}
      animate={{
        opacity: visible ? (active ? 1 : mathWhiteboard ? 0.88 : 0.82) : 0,
        y: 0,
        scale: 1,
        clipPath: teacherBoard
          ? `inset(0 ${visible ? (1 - writeProgress) * 100 : 100}% 0 0)`
          : writeOn ? (visible ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)') : 'inset(0 0% 0 0)',
        boxShadow: active && isNote
          ? (mathWhiteboard ? '0 0 0 6px rgba(37,99,235,.10)' : '0 0 0 7px rgba(255,221,96,.36), 4px 8px 0 rgba(31,65,93,.10)')
          : isNote && !mathWhiteboard ? '3px 5px 0 rgba(31,65,93,.08)' : '0 0 0 0 rgba(0,0,0,0)',
      }}
      transition={{ duration: teacherBoard ? 0 : writeOn ? 1.05 : 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      {element.type === 'arrow' ? (
        <svg viewBox="0 0 100 40" className="h-full w-full overflow-visible" aria-hidden="true">
          <path d="M3 20 C30 4 62 4 92 20" fill="none" stroke={element.color || '#ad4164'} strokeWidth="3" strokeLinecap="round" />
          <path d="M84 12 L94 20 L83 28" fill="none" stroke={element.color || '#ad4164'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : null}
      {element.type === 'diagram' ? (
        <div
          className={`lesson-diagram-panel h-full bg-white p-3 ${active ? 'lesson-diagram-active' : ''}`}
          role="img"
          aria-label={element.diagramData?.caption || element.text || `${element.diagramKind || 'Educational'} diagram`}
        >
          <Diagram element={element} darkMode={darkMode} />
        </div>
      ) : null}
      {element.type === 'table' ? (
        <div className="grid h-full overflow-hidden bg-white text-slate-900" style={{ gridTemplateColumns: `repeat(${Math.max(1, element.diagramData?.rows?.[0]?.length || 3)}, minmax(0, 1fr))` }}>
          {(element.diagramData?.rows?.flat() || ['Category', 'Value', 'Meaning', 'Example', 'Result', 'Check']).map((cell, index) => (
            <div key={index} className={`flex items-center border border-slate-300 text-[22px] ${teacherBoard ? `px-2 py-1 tabular-nums ${index % (element.diagramData?.rows?.[0]?.length || 3) === 0 ? 'justify-start' : 'justify-end'}` : 'justify-center p-3 text-center'} ${!teacherBoard && index < (element.diagramData?.rows?.[0]?.length || 3) ? 'font-bold' : ''}`}>{cell}</div>
          ))}
        </div>
      ) : null}
      {element.type === 'timeline' ? (
        <div className="relative flex h-full items-center justify-around">
          <div className="absolute left-[10%] right-[10%] top-1/2 h-2 bg-[#4878c0]" />
          {(element.diagramData?.stages?.length ? element.diagramData.stages : ['Context', 'Event', 'Impact', 'Legacy'].map((label): { label: string; detail?: string } => ({ label }))).slice(0, 5).map((stage, index) => (
            <div key={`${stage.label}-${index}`} className="relative z-10 flex max-w-[20%] flex-col items-center gap-3 text-center">
              <div className="h-12 w-12 rounded-full border-8 border-white bg-[#ad4124]" />
              <span className="text-[22px] font-bold text-slate-900">{stage.label}</span>
              {stage.detail && <span className="text-[22px] leading-tight text-slate-700">{stage.detail}</span>}
            </div>
          ))}
        </div>
      ) : null}
      {element.type === 'image' ? (
        <figure className="flex h-full w-full flex-col overflow-hidden bg-white p-4">
          <img
            src={element.imageUrl}
            alt={element.text || 'Original uploaded mathematics problem'}
            className="min-h-0 flex-1 object-contain"
          />
          <figcaption className="pt-3 text-center text-[22px] font-semibold text-neutral-500">
            {element.text || 'Educational illustration'}
          </figcaption>
        </figure>
      ) : null}
      {!['diagram', 'table', 'timeline', 'arrow', 'image'].includes(element.type) ? (
        <div className="h-full">
          {calloutLabel && element.type === 'note' && mathWhiteboard ? (
            <>
              <div className="mb-4 font-sans text-[0.68em] font-bold uppercase tracking-[0.14em] text-[#2563eb]">{calloutLabel}</div>
              <div>{renderFocusedText(element.text, active ? element.highlightText : '', element.highlightStyle)}</div>
            </>
          ) : isHeading ? <span className="relative inline-block px-3 py-1 text-[0.82em]">{element.text}</span> : renderFocusedText(element.text, active ? element.highlightText : '', element.highlightStyle)}
        </div>
      ) : null}
    </motion.div>
  );
}

export default function LessonVisualCanvas({ currentTime, lessonCanvas, title }: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState({ width: 900, height: 506 });
  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setViewport({ width: entry.contentRect.width, height: entry.contentRect.height }));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const beatIndex = lessonCanvas.beats.reduce((active, beat, index) => beat.startSeconds <= currentTime ? index : active, 0);
  const beat = lessonCanvas.beats[beatIndex] || lessonCanvas.beats[0];
  const teacherBoard = lessonCanvas.page.style === 'teacher-board';
  const revealed = useMemo(() => {
    const completedIds = lessonCanvas.beats.slice(0, beatIndex).flatMap((item) => item.revealIds);
    const currentIds = (beat?.revealIds || []).filter((id) => {
      if (teacherBoard) return currentTime - beat.startSeconds >= (beat.revealOffsetSeconds || 0);
      const element = lessonCanvas.elements.find((item) => item.id === id);
      if (element?.type !== 'equation' && element?.writingStyle !== 'handwritten') return true;
      return currentTime - beat.startSeconds >= 0.65;
    });
    return new Set([...completedIds, ...currentIds]);
  }, [lessonCanvas.beats, lessonCanvas.elements, beat, beatIndex, currentTime, teacherBoard]);
  const beatElapsed = beat ? currentTime - beat.startSeconds : 0;
  const highlightDelay = teacherBoard ? beat?.highlightOffsetSeconds || 0 : beat ? Math.min(1.5, Math.max(0.8, beat.durationSeconds * 0.3)) : 0;
  const highlighted = new Set(beatElapsed >= highlightDelay ? beat?.highlightIds || [] : []);
  const mathWhiteboard = lessonCanvas.page.style === 'math-whiteboard' || teacherBoard;
  const boardDark = false;
  const scale = Math.min(viewport.width / lessonCanvas.page.width, viewport.height / lessonCanvas.page.height);
  const x = (viewport.width - lessonCanvas.page.width * scale) / 2;
  const y = (viewport.height - lessonCanvas.page.height * scale) / 2;

  return (
    <div ref={viewportRef} className="absolute inset-0 overflow-hidden rounded-[inherit] bg-white" style={{ backgroundColor: '#FFFFFF' }} aria-label={`Progressively written lesson canvas for ${title}`}>
      <div
        className="lesson-whiteboard absolute left-0 top-0 origin-top-left overflow-hidden"
        style={{
          width: lessonCanvas.page.width,
          height: lessonCanvas.page.height,
          backgroundColor: '#FFFFFF',
          transform: `translate(${x}px, ${y}px) scale(${scale})`,
        }}
      >
        <div className="absolute right-20 top-14 lesson-label-type text-[17px] tracking-[.14em] text-slate-600">LAVYN · {teacherBoard ? 'LESSON' : mathWhiteboard ? 'MATH WHITEBOARD' : 'STUDY PAGE'}</div>
        {lessonCanvas.elements.map((element) => (
          <LessonElement
            key={element.id}
            element={element}
            visible={revealed.has(element.id)}
            active={highlighted.has(element.id)}
            mathWhiteboard={mathWhiteboard}
            darkMode={boardDark}
            teacherBoard={teacherBoard}
            writeProgress={(() => {
              if (!teacherBoard) return 1;
              const owner = lessonCanvas.beats.find(item => item.revealIds.includes(element.id));
              if (!owner) return 0;
              return Math.max(0, Math.min(1, (currentTime - owner.startSeconds - (owner.revealOffsetSeconds || 0)) / Math.max(.1, owner.writeDurationSeconds || 1)));
            })()}
          />
        ))}
      </div>
    </div>
  );
}